import React, { useContext, useMemo } from "react";
import { Container, Carousel, createClasses } from "@bloomberg/media-ui-core";
import { useLayer } from "@foundation/statsig";
import styles from "./watchlistBundleContainer.module.css";
import { PageContext } from "~context/PageContext";
import WatchlistBundle, { BundleSecurity } from "~components/WatchlistBundle";
import {
  CRYPTO_BUNDLE_SECURITIES,
  GLOBAL_MARKETS_BUNDLE_SECURITIES,
  TRENDING_BUNDLE_SECURITIES,
  AUTO_BUNDLE_SECURITIES,
  RETAIL_BUNDLE_SECURITIES,
  SEMICONDUCTORS_BUNDLE_SECURITIES,
  CRITICAL_MINERALS_BUNDLE_SECURITIES,
} from "~constants/Portfolio";
import { ONE_SECOND_IN_MILLISECONDS } from "~constants";
import { PortfolioPageData } from "~types/portfolio";
import { WatchlistBundleStyleVariant } from "~types/types";

const IMAGE_DOMAIN = process.env.NEXT_PUBLIC_ASSET_HOST ?? "assets.bwbx.io";
const IMAGE_ASSET_PREFIX = `https://${IMAGE_DOMAIN}/s3/bazaar/images`;
const BUNDLE_LIMIT = 7;
const TEN_SECONDS_IN_MILLISECONDS = ONE_SECOND_IN_MILLISECONDS * 10;

export const TRENDING_BUNDLE_HEADLINE = "Trending";
export const GLOBAL_MARKETS_BUNDLE_HEADLINE = "Global Markets";
export const CRYPTO_BUNDLE_HEADLINE = "Crypto";

export const AUTO_BUNDLE_HEADLINE = "Automotive";
export const RETAIL_BUNDLE_HEADLINE = "Retail";
export const SEMICONDUCTORS_BUNDLE_HEADLINE = "Semiconductors";
export const CRITICAL_MINERALS_BUNDLE_HEADLINE = "Critical Minerals";

type WatchlistBundleContainerProps = {
  maxWatchlistCountReached?: boolean;
  handleButtonClick?: () => void;
  variant?: WatchlistBundleStyleVariant;
};

type HeadlineTrackingConfigs = {
  [key: string]: [string, string, string, string];
};

type Bundle = {
  headline: string;
  banner: string;
  securities: BundleSecurity[];
  maxWatchlistCountReached?: boolean;
};

export const HEADLINE_TRACKING: HeadlineTrackingConfigs = {
  [TRENDING_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_trending_bundle",
    "click",
    "watchlist",
  ],
  [GLOBAL_MARKETS_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_global_markets_bundle",
    "click",
    "watchlist",
  ],
  [CRYPTO_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_crypto_bundle",
    "click",
    "watchlist",
  ],
  [AUTO_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_automotive_bundle",
    "click",
    "watchlist",
  ],
  [RETAIL_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_retail_bundle",
    "click",
    "watchlist",
  ],
  [SEMICONDUCTORS_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_semiconductors_bundle",
    "click",
    "watchlist",
  ],
  [CRITICAL_MINERALS_BUNDLE_HEADLINE as string]: [
    "watchlistCreate",
    "create_watchlist_critical_minerals_bundle",
    "click",
    "watchlist",
  ],
};

const WATCHLIST_BUNDLES: Bundle[] = [
  {
    headline: TRENDING_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/trending.jpg`,
    securities: TRENDING_BUNDLE_SECURITIES,
  },
  {
    headline: GLOBAL_MARKETS_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/global_markets.jpg`,
    securities: GLOBAL_MARKETS_BUNDLE_SECURITIES,
  },
  {
    headline: CRYPTO_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/crypto.jpg`,
    securities: CRYPTO_BUNDLE_SECURITIES,
  },
];

const TARIFF_BUNDLES: Bundle[] = [
  {
    headline: AUTO_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/automotive_bundle.jpg`,
    securities: AUTO_BUNDLE_SECURITIES,
  },
  {
    headline: RETAIL_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/retail_bundle.jpg`,
    securities: RETAIL_BUNDLE_SECURITIES,
  },
  {
    headline: SEMICONDUCTORS_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/semiconductors_bundle.jpg`,
    securities: SEMICONDUCTORS_BUNDLE_SECURITIES,
  },
  {
    headline: CRITICAL_MINERALS_BUNDLE_HEADLINE,
    banner: `${IMAGE_ASSET_PREFIX}/minerals_bundle.jpg`,
    securities: CRITICAL_MINERALS_BUNDLE_SECURITIES,
  },
];

export default function WatchlistBundleContainer(
  props: WatchlistBundleContainerProps
) {
  const { maxWatchlistCountReached, variant = "default" } = props;

  const pageContext = useContext(PageContext);

  const tariffBundlesLayer = useLayer("watchlist-dashboard-v2");
  const tariffBundlesFeatureEnabled =
    tariffBundlesLayer.get("enabled", false) ||
    Boolean((pageContext?.pageData as PortfolioPageData)?.enableTariffBundles);

  const WATCHLIST_BUNDLES_WITH_LIMIT = WATCHLIST_BUNDLES.map((bundle) => ({
    ...bundle,
    maxWatchlistCountReached,
  }));

  const containerClass = createClasses(styles.watchlistBundleContainer, {
    [styles.wrappedContainerModifier]: variant === "wrapped",
  });

  const bundleModuleClass = createClasses({
    [styles.bundleModule]: variant === "default",
    [styles.wrappedBundleModule]: variant === "wrapped",
  });

  const bundleMobileAndTabletClass = createClasses({
    [styles.bundleMobileAndTablet]: variant === "default",
    [styles.wrappedBundleMobileAndTablet]: variant === "wrapped",
  });

  const themeText = useMemo(() => {
    if (variant === "wrapped") {
      return "Start a watchlist to view the daily trends of assets you care about.";
    }
    if (tariffBundlesFeatureEnabled) {
      return "Track the impact of tariffs.";
    }

    return "Kickstart watchlists based on themes.";
  }, [variant, tariffBundlesFeatureEnabled]);

  const showSubscriberOnlyTag = useMemo(() => {
    return variant === "wrapped";
  }, [variant]);

  return (
    <Container rounded className={containerClass}>
      <div className={styles.watchlistBundleContent}>
        {showSubscriberOnlyTag && (
          <p className={styles.subscriberOnlyLabel}>Subscriber Only</p>
        )}
        <div className={styles.themeText}>{themeText}</div>
        <div className={styles.easyAddText}>
          You can easily add or remove symbols later.
        </div>
        <div className={bundleModuleClass}>
          {tariffBundlesFeatureEnabled
            ? TARIFF_BUNDLES.map((tariffBundle) => (
                <WatchlistBundle
                  {...tariffBundle}
                  key={tariffBundle.headline}
                  tariffBundlesFeatureEnabled
                  variant={variant}
                />
              ))
            : WATCHLIST_BUNDLES_WITH_LIMIT.map((watchlistBundle) => (
                <WatchlistBundle
                  {...watchlistBundle}
                  key={watchlistBundle.headline}
                  variant={variant}
                />
              ))}
        </div>
        <div className={bundleMobileAndTabletClass}>
          <Carousel
            limit={BUNDLE_LIMIT}
            animatedTransition
            autoadvanceIntervalInMs={TEN_SECONDS_IN_MILLISECONDS}
          >
            {tariffBundlesFeatureEnabled
              ? TARIFF_BUNDLES.map((tariffBundle) => (
                  <WatchlistBundle
                    {...tariffBundle}
                    key={tariffBundle.headline}
                    tariffBundlesFeatureEnabled
                    variant={variant}
                  />
                ))
              : WATCHLIST_BUNDLES.map((watchlistBundle, index) => (
                  <WatchlistBundle
                    {...watchlistBundle}
                    key={index}
                    variant={variant}
                  />
                ))}
          </Carousel>
        </div>
      </div>
    </Container>
  );
}
